import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import Logo from "@/components/Logo";
import LocationPicker from "@/components/LocationPicker";
import RoadrunnerAlert from "@/components/RoadrunnerAlert";
import SupportDrawer from "@/components/SupportDrawer";
import InJobChat from "@/components/InJobChat";
import RateDriverModal from "@/components/RateDriverModal";
import ReceiptModal from "@/components/ReceiptModal";
import LiveTrackingMap from "@/components/LiveTrackingMap";
import BecomeDriverButton from "@/components/BecomeDriverButton";
import SOSButton from "@/components/SOSButton";
import TipPicker from "@/components/TipPicker";
import TutorialOverlay from "@/components/TutorialOverlay";
import MobileHelpFab from "@/components/MobileHelpFab";
import { Truck, BatteryCharging, Disc, Fuel, Lock, Anchor, Car, LogOut, MapPin, Loader, CheckCircle2, Clock, DollarSign, LifeBuoy, Phone, Wrench, Star, Receipt as ReceiptIcon, AlertCircle, User, User as UserIcon } from "lucide-react";

const ICONS = { towing: Truck, jump_start: BatteryCharging, tire_change: Disc, fuel_delivery: Fuel, lockout: Lock, winch_out: Anchor, ride_transport: Car };

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800 border-amber-400",
  accepted: "bg-blue-100 text-asap-blue border-asap-blue",
  en_route: "bg-orange-100 text-asap-orange border-asap-orange",
  on_scene: "bg-violet-100 text-violet-800 border-violet-500",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-500",
  cancelled: "bg-slate-100 text-slate-600 border-slate-400",
};

export default function CustomerDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [step, setStep] = useState(1);
  const [selected, setSelected] = useState(null);
  const [location, setLocation] = useState(null);
  const [vehicleInfo, setVehicleInfo] = useState("");
  const [notes, setNotes] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [promoInfo, setPromoInfo] = useState(null);
  const [promoErr, setPromoErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [activeBooking, setActiveBooking] = useState(null);
  const [alertTrigger, setAlertTrigger] = useState(0);
  const [supportOpen, setSupportOpen] = useState(false);
  const [tipModal, setTipModal] = useState(null); // holds {bookingId, price}
  const [rateBooking, setRateBooking] = useState(null); // booking pending rating
  const [ratePromptedIds, setRatePromptedIds] = useState(() => new Set(JSON.parse(localStorage.getItem("asap_rated_prompted") || "[]")));
  const [receiptBooking, setReceiptBooking] = useState(null);
  const [tutorialOpen, setTutorialOpen] = useState(false);

  useEffect(() => {
    api.get("/services").then((r) => setServices((r.data || []).filter((s) => s.active !== false)));
    loadBookings();
    askForLocation();
    const interval = setInterval(loadBookings, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, []);

  // App Store / Play Store compliance: browsers show the native permission prompt with the site
  // origin only. Native wrappers (Capacitor/Expo) can override the prompt copy. This function is
  // called ONLY after the user taps "Locate Me" — never on load — so the "Why do you need location?"
  // rationale in the button label ("Use my location to dispatch the nearest driver") is what the
  // user sees right before the OS prompt. Do not move this to a mount effect.
  const askForLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => setLocation({ lat: 39.8283, lng: -98.5795 }) // Geographic center of USA — nationwide service default
    );
  };

  const loadBookings = async () => {
    try {
      // Drivers acting as customer must fetch their customer-side bookings, not their driver jobs.
      const params = user?.role === "driver" ? { mode: "customer" } : {};
      const { data } = await api.get("/bookings/my", { params });
      setBookings(data);
      const active = data.find((b) => ["pending", "accepted", "en_route", "on_scene"].includes(b.status));
      setActiveBooking(active || null);

      // Auto-prompt for ratings on completed+paid bookings that haven't been rated or skipped this session.
      const needsRating = data.find(
        (b) =>
          b.status === "completed" &&
          b.payment_status === "paid" &&
          b.driver_id &&
          !b.rating &&
          !ratePromptedIds.has(b.id)
      );
      if (needsRating && !rateBooking) setRateBooking(needsRating);
    } catch {}
  };

  const markRatePrompted = (bookingId) => {
    setRatePromptedIds((prev) => {
      const next = new Set(prev);
      next.add(bookingId);
      try {
        localStorage.setItem("asap_rated_prompted", JSON.stringify(Array.from(next)));
      } catch {}
      return next;
    });
  };

  const submitBooking = async () => {
    setError("");
    setBusy(true);
    try {
      const payload = {
        service_id: selected.id,
        pickup_address: `${location.lat.toFixed(5)}, ${location.lng.toFixed(5)}`,
        pickup_lat: location.lat,
        pickup_lng: location.lng,
        vehicle_info: vehicleInfo,
        notes,
        promo_code: promoCode || undefined,
      };
      const { data } = await api.post("/bookings", payload);
      setActiveBooking(data);
      setAlertTrigger((n) => n + 1);
      setStep(1);
      setSelected(null);
      setVehicleInfo("");
      setNotes("");
      setPromoCode("");
      setPromoInfo(null);
      loadBookings();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const payNow = async (bookingId) => {
    const b = bookings.find((x) => x.id === bookingId) || activeBooking;
    setTipModal({ bookingId, price: b?.price || 0 });
  };

  const confirmPay = async (tipAmount) => {
    if (!tipModal) return;
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        booking_id: tipModal.bookingId,
        origin_url: window.location.origin,
        tip_amount: tipAmount,
      });
      window.location.href = data.url;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setBusy(false);
      setTipModal(null);
    }
  };

  const cancelBooking = async (id) => {
    try {
      await api.post(`/bookings/${id}/status`, { status: "cancelled" });
      loadBookings();
    } catch {}
  };

  const doLogout = async () => {
    await logout();
    nav("/");
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="customer-dashboard">
      <MobileHelpFab requestPath="/customer" requestLabel="Book Rescue" />
      <TutorialOverlay role="customer" forceOpen={tutorialOpen} onClose={() => setTutorialOpen(false)} />
      <RoadrunnerAlert trigger={alertTrigger} />
      <header className="bg-white border-b-2 border-asap-deep sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 py-3 sm:py-5 flex justify-between items-center gap-2">
          <Logo size={56} />
          <div className="flex items-center gap-1 sm:gap-3 flex-wrap justify-end">
            {user?.role === "driver" && (
              <button
                onClick={() => nav("/driver")}
                data-testid="back-to-driver-btn"
                title="Back to Driver Mode"
                className="inline-flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 bg-asap-deep text-white text-xs sm:text-sm font-bold uppercase border-2 border-asap-deep hover:bg-asap-blue transition"
              >
                <Wrench size={14}/><span className="hidden md:inline"> Back to Driver Mode</span>
              </button>
            )}
            {user?.role === "customer" && <BecomeDriverButton />}
            <button onClick={() => setTutorialOpen(true)} data-testid="open-tutorial" className="inline-flex items-center gap-1 px-2 sm:px-3 py-2 border-2 border-asap-orange text-asap-orange text-[10px] sm:text-sm font-bold uppercase hover:bg-asap-orange hover:text-white transition">
              Tutorial
            </button>
            <a href="tel:+18317477141" title="Call 831-747-7141" aria-label="Call dispatch" className="inline-flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 border-2 border-asap-deep text-asap-deep text-xs sm:text-sm font-bold uppercase hover:bg-asap-deep hover:text-white" data-testid="header-call"><Phone size={14}/><span className="hidden md:inline"> 831-747-7141</span></a>
            <button onClick={() => setSupportOpen(true)} data-testid="open-support" title="Help" className="inline-flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 bg-asap-yellow text-asap-deep text-xs sm:text-sm font-bold uppercase border-2 border-asap-deep shadow-hard-sm hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none">
              <LifeBuoy size={14}/><span className="hidden sm:inline"> Help</span>
            </button>
            <div className="text-sm font-bold text-asap-deep hidden lg:block" data-testid="user-greeting">Hi, {user.name}</div>
            <button data-testid="logout-btn" onClick={doLogout} title="Logout" aria-label="Logout" className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 border-2 border-asap-deep text-asap-deep hover:bg-asap-deep hover:text-white text-xs sm:text-sm font-bold uppercase">
              <LogOut size={14}/><span className="hidden sm:inline"> Logout</span>
            </button>
            <button onClick={() => nav("/account")} data-testid="account-link" title="Account settings" aria-label="Account settings" className="flex items-center gap-1 px-2 py-2 border-2 border-asap-deep text-asap-deep hover:bg-asap-deep hover:text-white text-xs font-bold uppercase">
              <UserIcon size={14}/><span className="hidden sm:inline"> Account</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-3 sm:px-6 py-4 sm:py-8 grid lg:grid-cols-3 gap-4 sm:gap-8">
        <div className="lg:col-span-2 space-y-6">
          {activeBooking ? (
            <ActiveBookingCard booking={activeBooking} onPay={payNow} onCancel={cancelBooking} busy={busy} />
          ) : (
            <BookingFlow
              step={step} setStep={setStep}
              services={services} selected={selected} setSelected={setSelected}
              location={location} setLocation={setLocation}
              vehicleInfo={vehicleInfo} setVehicleInfo={setVehicleInfo}
              notes={notes} setNotes={setNotes}
              promoCode={promoCode} setPromoCode={setPromoCode}
              promoInfo={promoInfo} setPromoInfo={setPromoInfo}
              promoErr={promoErr} setPromoErr={setPromoErr}
              busy={busy} submitBooking={submitBooking}
              error={error}
            />
          )}
        </div>

        <aside>
          <div className="bg-white border-2 border-asap-deep p-5 shadow-hard">
            <h3 className="font-display uppercase text-asap-deep mb-3">Your Trips</h3>
            <div className="space-y-3 max-h-[500px] overflow-y-auto" data-testid="trips-list">
              {bookings.length === 0 && <div className="text-sm text-slate-400">No trips yet.</div>}
              {bookings.map((b) => (
                <div key={b.id} className="border border-slate-200 p-3 rounded-md" data-testid={`trip-${b.id}`}>
                  <div className="flex justify-between text-sm">
                    <div className="font-bold text-asap-deep">{b.service_name}</div>
                    <div className="font-display text-asap-orange">${b.price.toFixed(2)}</div>
                  </div>
                  <div className={`inline-block mt-2 text-[10px] px-2 py-0.5 border ${STATUS_COLORS[b.status]} font-bold uppercase tracking-wider`}>{b.status}</div>
                  <div className="text-[11px] text-slate-500 mt-1">{new Date(b.created_at).toLocaleString()}</div>
                  {b.rating && (
                    <div className="mt-2 inline-flex items-center gap-1 text-xs text-asap-deep" data-testid={`trip-rating-${b.id}`}>
                      <span className="font-bold">Your rating:</span>
                      <span className="inline-flex text-asap-orange">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star key={i} size={12} strokeWidth={2.5} fill={i < b.rating ? "currentColor" : "none"}/>
                        ))}
                      </span>
                    </div>
                  )}
                  <div className="flex gap-1 flex-wrap mt-2">
                    {b.status === "completed" && b.payment_status === "paid" && (
                      <button
                        onClick={() => setReceiptBooking(b)}
                        data-testid={`receipt-trip-${b.id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-asap-deep text-white border-2 border-asap-deep text-[11px] font-bold uppercase tracking-wider hover:bg-asap-blue transition"
                      >
                        <ReceiptIcon size={12}/> Receipt
                      </button>
                    )}
                    {b.status === "completed" && b.payment_status === "paid" && !b.rating && b.driver_id && (
                      <button
                        onClick={() => setRateBooking(b)}
                        data-testid={`rate-trip-${b.id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-asap-yellow text-asap-deep border-2 border-asap-deep text-[11px] font-bold uppercase tracking-wider hover:translate-x-[1px] hover:translate-y-[1px] transition"
                      >
                        <Star size={12} strokeWidth={2.5}/> Rate driver
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </main>
      <SupportDrawer open={supportOpen} onClose={() => setSupportOpen(false)} />
      <TipPicker
        open={!!tipModal}
        servicePrice={tipModal?.price || 0}
        onClose={() => { setTipModal(null); setBusy(false); }}
        onConfirm={confirmPay}
        busy={busy}
      />
      <RateDriverModal
        booking={rateBooking}
        onClose={() => {
          if (rateBooking) markRatePrompted(rateBooking.id);
          setRateBooking(null);
        }}
        onRated={() => { if (rateBooking) markRatePrompted(rateBooking.id); loadBookings(); }}
      />
      <ReceiptModal open={!!receiptBooking} booking={receiptBooking} onClose={() => setReceiptBooking(null)} />
    </div>
  );
}

function ActiveBookingCard({ booking, onPay, onCancel, busy }) {
  const Icon = ICONS[booking.service_id] || Car;
  const [driverInfo, setDriverInfo] = useState(null);
  const [elapsedMin, setElapsedMin] = useState(0);

  useEffect(() => {
    if (!booking.driver_id) { setDriverInfo(null); return; }
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get(`/bookings/${booking.id}/driver-location`);
        if (!cancelled) setDriverInfo(data);
      } catch {}
    };
    load();
    const t = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, [booking.driver_id, booking.id]);

  useEffect(() => {
    const tick = () => {
      if (!booking.created_at) return;
      const mins = (Date.now() - new Date(booking.created_at).getTime()) / 60000;
      setElapsedMin(mins);
    };
    tick();
    const t = setInterval(tick, 15000);
    return () => clearInterval(t);
  }, [booking.created_at]);

  const showFallback = booking.status === "pending" && !booking.driver_id && elapsedMin >= 5;

  return (
    <div className="bg-white border-2 border-asap-deep shadow-hard" data-testid="active-booking">
      <div className="p-6 border-b-2 border-asap-deep flex items-center gap-4 bg-gradient-to-r from-asap-blue to-asap-deep text-white">
        <div className="w-14 h-14 bg-asap-yellow text-asap-deep flex items-center justify-center"><Icon size={28} strokeWidth={2.5}/></div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-[0.3em] text-asap-yellow font-bold">Active Request</div>
          <div className="font-display text-2xl uppercase">{booking.service_name}</div>
        </div>
        <div className="text-right">
          <div className="font-display text-3xl text-asap-yellow">${booking.price.toFixed(2)}</div>
          <div className={`inline-block mt-1 text-[10px] px-2 py-0.5 border ${STATUS_COLORS[booking.status]} font-bold uppercase tracking-wider`} data-testid="active-status">{booking.status}</div>
        </div>
      </div>

      {/* Emergency fallback — auto-appears if pending >5 min with no driver */}
      {showFallback && (
        <div className="px-6 py-4 bg-asap-red text-white flex flex-col sm:flex-row items-start sm:items-center gap-3" data-testid="no-driver-fallback">
          <div className="flex-1">
            <div className="font-display uppercase text-lg">We're locating a Roadrunner.</div>
            <div className="text-xs text-white/90">Call or text dispatch for immediate assistance.</div>
          </div>
          <div className="flex gap-2">
            <a href="tel:+18317477141" data-testid="fallback-call" className="px-3 py-2 bg-white text-asap-red font-bold uppercase text-xs inline-flex items-center gap-1" title="Call 831-747-7141"><Phone size={14}/> Call</a>
            <a href="sms:+17653301951" data-testid="fallback-text" className="px-3 py-2 bg-asap-deep text-white font-bold uppercase text-xs inline-flex items-center gap-1" title="Text 765-330-1951"><Phone size={14}/> Text</a>
          </div>
        </div>
      )}

      {/* Driver profile bar — trust/safety */}
      {booking.driver_id && (
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center gap-4" data-testid="driver-profile-bar">
          <div className="w-14 h-14 rounded-full bg-asap-yellow flex items-center justify-center overflow-hidden border-2 border-asap-deep shrink-0">
            {driverInfo?.driver_photo_url ? (
              <img src={driverInfo.driver_photo_url} alt="Driver" className="w-full h-full object-cover"/>
            ) : (
              <User size={26} strokeWidth={2.5} className="text-asap-deep"/>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg text-asap-deep leading-tight">{booking.driver_name || "Your rescuer"}</div>
            <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
              {(driverInfo?.vehicle) && <span className="inline-flex items-center gap-1"><Truck size={11}/> {driverInfo.vehicle}</span>}
              {driverInfo?.driver_rating != null && <span className="inline-flex items-center gap-1">★ {driverInfo.driver_rating.toFixed(1)}</span>}
              {driverInfo?.has_location && driverInfo.eta_minutes != null && (
                <span className="inline-flex items-center gap-1 text-asap-orange font-bold" data-testid="eta-chip">
                  <Clock size={11}/> ~{driverInfo.eta_minutes} min · {driverInfo.miles} mi
                </span>
              )}
            </div>
          </div>
          {booking.customer_phone && booking.driver_id && (
            <a href={`tel:${booking.customer_phone}`} className="hidden" aria-hidden="true">.</a>
          )}
          <SOSButton bookingId={booking.id} driverName={booking.driver_name}/>
        </div>
      )}

      <div className="p-6 grid sm:grid-cols-2 gap-6">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-asap-deep mb-2">Live Location</div>
          {booking.driver_id ? (
            <LiveTrackingMap booking={booking} height={260}/>
          ) : (
            <LocationPicker value={{ lat: booking.pickup_lat, lng: booking.pickup_lng }} readOnly height={260} />
          )}
        </div>
        <div className="space-y-3 text-sm">
          <Row label="Driver" value={booking.driver_name || "Searching…"} />
          <Row label="Vehicle" value={booking.vehicle_info || "—"} />
          <Row label="Notes" value={booking.notes || "—"} />
          <Row label="Payment" value={booking.payment_status} />
        </div>
      </div>

      {booking.driver_id && (
        <div className="px-6 pb-6">
          <InJobChat bookingId={booking.id} otherName={booking.driver_name} />
        </div>
      )}

      <div className="p-6 border-t border-slate-200 bg-slate-50 flex flex-wrap gap-3">
        {booking.status !== "cancelled" && booking.payment_status !== "paid" && (
          <button data-testid="pay-now-btn" disabled={busy} onClick={() => onPay(booking.id)} className="px-5 py-3 bg-asap-yellow text-asap-deep font-display uppercase border-2 border-asap-deep shadow-hard hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-hard-sm transition-all disabled:opacity-60 inline-flex items-center gap-2">
            <DollarSign size={18}/> Pay ${booking.price.toFixed(2)} Now
          </button>
        )}
        {booking.status === "pending" && (
          <button data-testid="cancel-booking-btn" onClick={() => onCancel(booking.id)} className="px-5 py-3 border-2 border-asap-red text-asap-red font-bold uppercase hover:bg-asap-red hover:text-white">
            Cancel
          </button>
        )}
        {booking.payment_status === "paid" && booking.status !== "completed" && (
          <div className="px-5 py-3 bg-emerald-50 border-2 border-emerald-500 text-emerald-700 font-bold uppercase inline-flex items-center gap-2"><CheckCircle2 size={18}/> Paid</div>
        )}
      </div>
      {booking.status === "pending" && (
        <div className="px-6 pb-6 flex items-start gap-2 text-[11px] text-slate-500 border-t border-slate-100 pt-3">
          <AlertCircle size={12} className="shrink-0 mt-0.5"/>
          <span><span className="font-bold text-asap-deep">Cancellation policy:</span> Free to cancel while pending. Once a driver accepts, you may cancel but a $10 fee may apply if the driver is already en route.</span>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex border-b border-slate-100 pb-2">
      <div className="w-24 text-xs uppercase tracking-widest font-bold text-asap-deep">{label}</div>
      <div className="flex-1 text-asap-deep capitalize">{value}</div>
    </div>
  );
}

function BookingFlow({ step, setStep, services, selected, setSelected, location, setLocation, vehicleInfo, setVehicleInfo, notes, setNotes, promoCode, setPromoCode, promoInfo, setPromoInfo, promoErr, setPromoErr, busy, submitBooking, error }) {
  const applyPromo = async () => {
    if (!promoCode) return;
    setPromoErr("");
    try {
      const { data } = await api.post("/promos/validate", { code: promoCode });
      setPromoInfo(data);
    } catch (e) {
      setPromoInfo(null);
      setPromoErr(e.response?.data?.detail || "Invalid promo code");
    }
  };
  const clearPromo = () => { setPromoCode(""); setPromoInfo(null); setPromoErr(""); };
  const price = selected?.price || 0;
  const discount = promoInfo ? (price * (promoInfo.percent_off || 0) / 100 + (promoInfo.flat_off || 0)) : 0;
  const finalPrice = Math.max(0, price - discount);
  return (
    <div className="bg-white border-2 border-asap-deep shadow-hard">
      <div className="p-6 border-b-2 border-slate-100">
        <div className="text-xs uppercase tracking-[0.3em] text-asap-orange font-bold">Step {step} of 3</div>
        <h2 className="font-display text-2xl uppercase text-asap-deep mt-1">
          {step === 1 && "Pick a service"}
          {step === 2 && "Drop the pin"}
          {step === 3 && "Almost there"}
        </h2>
      </div>

      <div className="p-6">
        {step === 1 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="service-grid">
            {services.map((s) => {
              const Icon = ICONS[s.id] || Car;
              const active = selected?.id === s.id;
              return (
                <button
                  key={s.id}
                  data-testid={`service-tile-${s.id}`}
                  onClick={() => setSelected(s)}
                  className={`service-tile p-5 border-2 border-asap-deep bg-white text-left ${active ? "active" : ""}`}
                >
                  <Icon size={28} strokeWidth={2.5} className="text-asap-orange"/>
                  <div className="font-display uppercase text-sm mt-3 text-asap-deep">{s.name}</div>
                  <div className="text-[11px] text-slate-500 mt-1 leading-tight">{s.desc}</div>
                  <div className="font-display text-asap-orange mt-2">${s.price.toFixed(0)}</div>
                </button>
              );
            })}
          </div>
        )}

        {step === 2 && (
          <div>
            <div className="text-sm text-slate-600 mb-3 flex items-center gap-2"><MapPin size={16}/> Tap the map to drop your pin.</div>
            <LocationPicker value={location} onChange={setLocation} height={360} />
            {location && (
              <div className="mt-3 text-xs text-asap-deep font-bold">
                Lat: {location.lat.toFixed(5)} · Lng: {location.lng.toFixed(5)}
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <Field label="Vehicle (year/make/model)">
              <input data-testid="booking-vehicle" value={vehicleInfo} onChange={(e) => setVehicleInfo(e.target.value)} className="w-full px-4 py-3 border-2 border-asap-deep focus:border-asap-orange outline-none" placeholder="2018 Honda Civic, silver" />
            </Field>
            <Field label="Notes for driver">
              <textarea data-testid="booking-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="w-full px-4 py-3 border-2 border-asap-deep focus:border-asap-orange outline-none" placeholder="Front tire passenger side flat" />
            </Field>
            <Field label="Promo code (optional)">
              {promoInfo ? (
                <div className="flex items-center gap-2 p-3 border-2 border-emerald-500 bg-emerald-50" data-testid="promo-applied">
                  <CheckCircle2 size={18} className="text-emerald-600"/>
                  <div className="flex-1">
                    <div className="font-display uppercase text-emerald-800">{promoInfo.code} applied</div>
                    <div className="text-xs text-emerald-700">
                      {promoInfo.percent_off ? `${promoInfo.percent_off}% off` : ""} {promoInfo.flat_off ? `$${promoInfo.flat_off} off` : ""}
                    </div>
                  </div>
                  <button onClick={clearPromo} className="text-xs font-bold text-asap-red uppercase" data-testid="promo-remove">Remove</button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input data-testid="booking-promo" value={promoCode} onChange={(e) => setPromoCode(e.target.value.toUpperCase())} className="flex-1 px-4 py-3 border-2 border-asap-deep focus:border-asap-orange outline-none uppercase tracking-widest font-bold" placeholder="ASAPFRIEND" />
                  <button onClick={applyPromo} disabled={!promoCode} data-testid="booking-promo-apply" className="px-4 py-3 bg-asap-deep text-white font-display uppercase text-sm disabled:opacity-40">Apply</button>
                </div>
              )}
              {promoErr && <div className="text-asap-red text-xs mt-1 font-bold" data-testid="promo-error">{promoErr}</div>}
            </Field>
            <div className="bg-asap-yellow/20 border-2 border-asap-yellow p-4">
              <div className="text-xs uppercase tracking-widest font-bold text-asap-deep">Summary</div>
              <div className="flex justify-between mt-2 items-end">
                <div className="font-display text-xl uppercase text-asap-deep">{selected?.name}</div>
                <div className="text-right">
                  {promoInfo && <div className="text-xs text-slate-500 line-through">${price.toFixed(2)}</div>}
                  <div className="font-display text-3xl text-asap-orange" data-testid="booking-final-price">${finalPrice.toFixed(2)}</div>
                </div>
              </div>
              {promoInfo && (
                <div className="text-xs text-emerald-700 font-bold mt-1" data-testid="booking-discount">You save ${discount.toFixed(2)}</div>
              )}
              <div className="text-[11px] text-slate-600 mt-1">{location && `${location.lat.toFixed(5)}, ${location.lng.toFixed(5)}`}</div>
            </div>
          </div>
        )}

        {error && <div className="mt-4 text-asap-red text-sm font-bold border-2 border-asap-red bg-red-50 px-3 py-2">{error}</div>}
      </div>

      <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-between">
        <button data-testid="flow-back" disabled={step === 1} onClick={() => setStep(step - 1)} className="px-5 py-2 border-2 border-asap-deep text-asap-deep font-bold uppercase disabled:opacity-30">← Back</button>
        {step < 3 ? (
          <button
            data-testid="flow-next"
            disabled={(step === 1 && !selected) || (step === 2 && !location)}
            onClick={() => setStep(step + 1)}
            className="px-7 py-2 bg-asap-yellow text-asap-deep font-display uppercase border-2 border-asap-deep shadow-hard-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all disabled:opacity-40"
          >
            Continue →
          </button>
        ) : (
          <button data-testid="flow-submit" onClick={submitBooking} disabled={busy} className="px-7 py-2 bg-asap-red text-white font-display uppercase border-2 border-asap-deep shadow-hard-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all disabled:opacity-60 inline-flex items-center gap-2">
            {busy ? <Loader size={16} className="animate-spin"/> : <Clock size={16}/>}
            {busy ? "Dispatching…" : "Send Request"}
          </button>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs tracking-[0.2em] uppercase font-bold text-asap-deep mb-1">{label}</div>
      {children}
    </label>
  );
}
